import { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { TaskHelpers } from 'ontraccr-common';

import { loadTasks } from '../../clock/ManualEntry/manualEntryHelpers';
import Debouncer from '../../helpers/Debouncer';

const EMPTY_MAP = {};

const getRelevantEntries = (entries) => (
  entries.filter((entry) => entry.userId && entry.dateTimestamp)
);

const detectConflicts = async (relevantEntries, {
  showOriginalTimes,
  detectHourBasedConflict,
}) => {
  const dates = relevantEntries
    .map((entry) => TaskHelpers.getTaskDate(entry))
    .filter(Boolean);
  const existing = await loadTasks({
    startDate: DateTime.fromISO(Math.min(...dates)).toISODate(),
    endDate: DateTime.fromISO(Math.max(...dates)).toISODate(),
    userIds: Array.from(new Set(relevantEntries.map((entry) => entry.userId))),
  });

  const conflicts = {};
  relevantEntries.forEach((entry) => {
    const currentTasks = existing.filter((task) => task.userId === entry.userId);
    if (!currentTasks.length) return;
    const hasConflict = TaskHelpers.hasTaskConflict({
      editIds: new Set([entry.id]),
      currentTasks,
      newTasks: [entry],
      showOriginalTimes,
      detectHourBasedConflict,
    });
    if (hasConflict) {
      if (hasConflict.hourBased) {
        const key = TaskHelpers.getHourBasedKey(entry);
        const existingTask = currentTasks.find((task) => (
          task.hourBased
          && task.id !== entry.id
          && TaskHelpers.getHourBasedKey(task) === key
        ));
        conflicts[entry.id] = existingTask ?? hasConflict;
      } else {
        conflicts[entry.id] = hasConflict;
      }
    }
  });
  return conflicts;
};

export default function useTimeEntryTableConflicts({
  fieldId,
  isDisplay,
  entries = [],
  formId,
  steps,
  templateSchema,
  showOriginalTimes,
  detectHourBasedConflict,
}) {
  const [conflictMap, setConflictMap] = useState(EMPTY_MAP);
  const debouncerRef = useRef(new Debouncer());

  const createTimeHasRun = steps?.some((step) => step.name === 'createTime');

  const hasPreventDuplicates = useMemo(() => {
    const nodes = templateSchema?.workflow?.nodes ?? [];
    return nodes.some((node) => (
      node.data?.tableFieldId === fieldId
      && node.type === 'createTime'
      && node.data?.preventDuplicates
    ));
  }, [fieldId, templateSchema]);

  useEffect(() => {
    const debouncer = debouncerRef.current;
    if (!isDisplay || !formId || createTimeHasRun || !hasPreventDuplicates) {
      setConflictMap(EMPTY_MAP);
      debouncer.clear();
      return;
    }

    let cancelled = false;
    const detect = async () => {
      const relevantEntries = getRelevantEntries(entries);
      if (!relevantEntries.length) {
        setConflictMap(EMPTY_MAP);
        return;
      }
      const nextMap = await detectConflicts(relevantEntries, {
        showOriginalTimes,
        detectHourBasedConflict,
      });

      if (!cancelled) setConflictMap(nextMap);
    };

    debouncer.debounce(detect, 300);
    return () => {
      cancelled = true;
      debouncer.clear();
    };
  }, [
    isDisplay,
    formId,
    createTimeHasRun,
    hasPreventDuplicates,
    showOriginalTimes,
    detectHourBasedConflict,
  ]);

  return conflictMap;
}
